import React, { useState, useEffect } from 'react';
import { useReportPermissions } from '../../../hooks/useReportPermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import axios from 'axios';
import ProductProfitReportFilter from './ProductProfitReportFilter';
import ProductProfitReportTable from './ProductProfitReportTable';

interface FilterData {
  invoiceType: string;
  invoicePages: string[];
  branchId: string;
  dateFrom: string;
  dateTo: string;
  productName: string;
  barcode: string;
  costType: 'last' | 'average';
}

interface ProductProfitData {
  _id: string;
  productName: string;
  variantAttributes: { [key: string]: string };
  barcode: string;
  totalQuantity: number;
  unitName: string;
  branchName?: string;
  salesValue: number;
  cost: number;
  profit: number;
}

const ProductProfitReport: React.FC = () => {
  const { hasReportPagePermission, hasReportExportPermission } = useReportPermissions();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [profitData, setProfitData] = useState<ProductProfitData[]>([]);
  const [filterValues, setFilterValues] = useState<FilterData>({
    invoiceType: '',
    invoicePages: [],
    branchId: '',
    dateFrom: '',
    dateTo: '',
    productName: '',
    barcode: '',
    costType: 'last'
  });

  const fetchData = async (filters: FilterData) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (filters.invoiceType) params.invoiceType = filters.invoiceType;
      if (filters.invoicePages && filters.invoicePages.length > 0) {
        params.invoicePages = filters.invoicePages.join(',');
      }
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.productName) params.productName = filters.productName;
      if (filters.barcode) params.barcode = filters.barcode;
      if (filters.costType) params.costType = filters.costType;

      const response = await axios.get('/api/reports/product-profit', { params });

      if (response.data && response.data.success) {
        setProfitData(response.data.data || []);
      } else {
        setProfitData([]);
      }
    } catch (err: any) {
      console.error('خطأ في جلب بيانات تقرير ربح المنتجات:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل البيانات');
      setProfitData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilterValues: FilterData) => {
    setFilterValues(newFilterValues);
    fetchData(newFilterValues);
  };

  const exportToExcel = async () => {
    if (!hasReportExportPermission('product-profit')) {
      setError('ليس لديك صلاحية لتصدير التقرير');
      return;
    }

    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('ربح المنتجات');

      const headers = [
        'اسم المنتج',
        'خصائص المتغير',
        'الباركود',
        'إجمالي الكمية',
        'قيمة المبيعات',
        `التكلفة (${filterValues.costType === 'last' ? 'آخر تكلفة' : 'متوسط التكلفة'})`,
        'الربح',
        'الفرع'
      ];

      worksheet.addRow(headers);
      worksheet.getRow(1).font = { bold: true, size: 12 };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      const getVariantText = (attributes: { [key: string]: string }): string => {
        if (!attributes) return '-';
        return Object.entries(attributes)
          .filter(([key]) => key !== 'barcode' && key !== 'باركود')
          .map(([_, value]) => value)
          .filter(value => value)
          .join(' - ') || '-';
      };

      profitData.forEach(item => {
        const row = [
          item.productName,
          getVariantText(item.variantAttributes),
          item.barcode || '-',
          `${item.totalQuantity} ${item.unitName}`,
          item.salesValue.toFixed(2),
          item.cost.toFixed(2),
          item.profit.toFixed(2),
          item.branchName || '-'
        ];
        worksheet.addRow(row);
      });

      const totalSales = profitData.reduce((sum, item) => sum + item.salesValue, 0);
      const totalCost = profitData.reduce((sum, item) => sum + item.cost, 0);
      const totalProfit = profitData.reduce((sum, item) => sum + item.profit, 0);

      worksheet.addRow([]);
      const totalRow = worksheet.addRow([
        'الإجمالي',
        '',
        '',
        '',
        totalSales.toFixed(2),
        totalCost.toFixed(2),
        totalProfit.toFixed(2),
        ''
      ]);
      totalRow.font = { bold: true };
      totalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      worksheet.columns.forEach(column => {
        column.width = 20;
        column.alignment = { vertical: 'middle', horizontal: 'right' };
      });

      worksheet.views = [{ rightToLeft: true }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_ربح_المنتجات_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('خطأ في تصدير البيانات إلى إكسل:', error);
      alert('حدث خطأ أثناء تصدير البيانات إلى إكسل');
    }
  };

  if (loading && profitData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mr: 2 }}>
          جاري تحميل البيانات...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          تقرير ربح المنتجات
        </Typography>
        {hasReportExportPermission('product-profit') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={profitData.length === 0}
            sx={{
              bgcolor: '#30c3b0',
              '&:hover': { bgcolor: '#25a899' },
              boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
            }}
          >
            تصدير إلى إكسل
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <AlertTitle>خطأ</AlertTitle>
          {error}
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ color: '#30c3b0', mr: 1 }} />
          <Typography variant="h6" component="h3">
            تصفية البيانات
          </Typography>
        </Box>
        <ProductProfitReportFilter
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
        />
      </Paper>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductProfitReportTable data={profitData} costType={filterValues.costType} />
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          إجمالي النتائج: {profitData.length} منتج
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductProfitReport;
